import React, { useEffect, useState } from "react";
import axios from '../../services/axios';
import { useStoreActions, useStoreState } from "easy-peasy";
import HelperMetiers360 from "../../services/HelpersMetiers360";
import ChartComponent from "../webappStats/ChartComponent";
import { Button, Col, Row, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import Loader from "../widgets/Loader";
import DatePicker, { registerLocale } from "react-datepicker";
import { fr } from 'date-fns/locale/fr';
import SelectMultipleItemsComponent from "../widgets/selectMultipleItemsComponent/SelectMultipleItemsComponent";
import './WebappGlobalStatComponent.scss';
import { Link } from "react-router-dom";

registerLocale('fr', fr);

const WebappGlobalStatComponent = () => {
    const { apiData: { ACTIONS } } = useStoreState(state => state.actionSlugs);
    const { clientsCategories } = useStoreState(state => state.clients);
    const { fetchClientsCategories } = useStoreActions(actions => actions.clients);
    const romesSlugs = useStoreState(state => state.actionSlugs.actionSlugsDispatcher('romes'));
    const clientSlugs = useStoreState(state => state.actionSlugs.actionSlugsDispatcher('clients'));

    const [isFetching, setIsFetching] = useState(false);
    const [graphsData, setGraphsData] = useState({});
    const [globalCount, setGlobalCount] = useState({});
    const [isAvailableData, setIsAvailableData] = useState({});
    const [romeTypeToDisplay, setRomeTypeToDisplay] = useState("découverts");
    const [minDate, setMinDate] = useState();
    const [maxDate, setMaxDate] = useState();
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [datesFilter, setDatesFilter] = useState([undefined, undefined]);
    const [categoriesFilter, setCategoriesFilter] = useState([]);

    const fetchDataForGraph = async () => {
        if (!ACTIONS?.webappStats?.global || isFetching) return;

        setIsFetching(true);
        const entries = Object.entries(ACTIONS.webappStats.global);

        try {
            const results = await Promise.allSettled(
                entries.map(async ([key, stat]) => {
                    if (!stat.url) return [key, null];
                    const response = await axios.post(stat.url, {
                        categories: categoriesFilter,
                        startDate: datesFilter[0],
                        endDate: datesFilter[1]
                    }, { responseType: "json" });
                    return [key, response];
                })
            );

            const dataObject = Object.fromEntries(results.filter(r => r.status === "fulfilled").map(r => r.value));
            const graphDataObject = Object.fromEntries(Object.entries(dataObject).map(([name, value]) => [name, value?.length ? value : []]));
            const isAvailableObject = Object.fromEntries(Object.entries(dataObject).map(([name, value]) => [name, !!value?.length]));

            setGraphsData(graphDataObject);
            setIsAvailableData(isAvailableObject);
        } catch (error) {
            Promise.reject(new Error(error));
        } finally {
            setIsFetching(false);
        }
    };

    const fetchGlobalCount = async () => {
        const getUrl = ACTIONS?.webappStats?.globalCount?.url;
        if (!getUrl) return;

        try {
            const responseGlobalCount = await axios.get(
                getUrl,
                { responseType: "json" }
            );
            let newGlobalCount = {};
            responseGlobalCount.forEach(el => {
                newGlobalCount[el.data] = el.count
            });
            setGlobalCount(newGlobalCount)
        } catch (error) {
            Promise.reject(new Error(error));
        }
    }

    useEffect(() => {
        fetchDataForGraph();
        fetchGlobalCount();
        if (clientsCategories.length == 0) {
            fetchClientsCategories();
        };
    }, []);

    useEffect(() => {
        fetchDataForGraph();
    }, [datesFilter, categoriesFilter]);


    const dateFilter = <div>
        <label className="label-select">Période&nbsp;:</label>
        <div className="d-flex align-items-center">
            <Button className="shadow btnInitFilters ms-0" variant="danger" size="sm" onClick={() => setMinDate(null)}>
                <i className="fas fa-redo"></i>
            </Button>
            <DatePicker
                showIcon
                locale='fr'
                id='start'
                dateFormat="dd/MM/yyyy"
                selected={minDate}
                onChange={(date) => setMinDate(date)}
                placeholderText="jj/mm/aaaa"
                maxDate={maxDate}
            />
            <label htmlFor="end" className="m-2">&nbsp;au</label>
            <Button className="shadow btnInitFilters" variant="danger" size="sm" onClick={() => setMaxDate(null)}>
                <i className="fas fa-redo"></i>
            </Button>
            <DatePicker
                showIcon
                locale='fr'
                id='end'
                dateFormat="dd/MM/yyyy"
                selected={maxDate}
                onChange={(date) => setMaxDate(date)}
                placeholderText="jj/mm/aaaa"
                minDate={minDate}
            />
        </div>
    </div>;

    const categoryFilter = clientsCategories?.length > 0 && <div>
        <label className="label-select">Catégories&nbsp;d'établissement&nbsp;: </label>
        <SelectMultipleItemsComponent itemsSelected={selectedCategories} setItemsSelected={setSelectedCategories}
            items={clientsCategories} name="contacts" itemKey="id" itemValue="name" id='select-category' />
    </div>;

    const deviceTypesLegendNewlabels = {
        "": "Autre",
        "desktop": "Ordinateur",
        "smartphone": "Mobile",
        "tablet": "Tablette",
        "phablet": "Phablette"
    };

    const tableCsv = {
        deviceTypes: graphsData?.deviceTypes?.map(data => {
            return {
                device: deviceTypesLegendNewlabels[data.device],
                count_sessions: data.count_sessions
            }
        })?.sort((a, b) =>
            HelperMetiers360.localeCompareWithNullable(a.count_sessions, b.count_sessions)),
        discoveredRomes: graphsData?.discoveredRomes?.sort((a, b) =>
            HelperMetiers360.localeCompareWithNullable(a.count_discovered, b.count_discovered)),
        favoriteRomes: graphsData?.favoriteRomes?.sort((a, b) =>
            HelperMetiers360.localeCompareWithNullable(a.count_favorite, b.count_favorite)),
        activities: graphsData?.activities?.sort((a, b) =>
            HelperMetiers360.localeCompareWithNullable(a.done_count + a.current_count, b.done_count + b.current_count)),
        activeUsersByClient: graphsData?.activeUsersByClient?.sort((a, b) =>
            HelperMetiers360.localeCompareWithNullable(a.count, b.count)),
    };

    const csvSort = {
        deviceTypes: [
            {
                value: 'device',
                label: 'Type d\'appareil',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.deviceTypes, "device"),
                display: (data) => data.device
            },
            {
                value: 'count_sessions',
                label: 'Nombre de connexions',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.deviceTypes, "count_sessions"),
                display: (data) => data.count_sessions
            }
        ],
        discoveredRomes: [
            {
                value: 'code_rome',
                label: 'Code ROME',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.discoveredRomes, "code_rome"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.code_rome, b.code_rome),
                display: (data) => romesSlugs?.readOne
                    ? <>
                        <Link to={romesSlugs?.readOne?.replace(':uniqueId', data.code_rome)}>
                            {data.code_rome}
                        </Link>
                    </>
                    : <>{data.code_rome ?? '-'}</>,
                flatDisplay: (data) => data.code_rome ?? '-'
            },
            {
                value: 'label',
                label: 'Nom ROME',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.discoveredRomes, "label"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.label, b.label),
                display: (data) => data.label
            },
            {
                value: 'count',
                label: 'Nombre d\'utilisateur⋅ice',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.discoveredRomes, "count_discovered"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.count_discovered, b.count_discovered),
                display: (data) => data.count_discovered
            }
        ],
        favoriteRomes: [
            {
                value: 'code_rome',
                label: 'Code ROME',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.favoriteRomes, "code_rome"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.code_rome, b.code_rome),
                display: (data) => data.code_rome
            },
            {
                value: 'label',
                label: 'Nom ROME',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.favoriteRomes, "label"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.label, b.label),
                display: (data) => data.label
            },
            {
                value: 'count',
                label: 'Nombre de favoris',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.favoriteRomes, "count_favorite"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.count_favorite, b.count_favorite),
                display: (data) => data.count_favorite
            }
        ],
        activities: [
            {
                value: 'activityTitle',
                label: 'Activité',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.activities, "activityTitle"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.activityTitle, b.activityTitle),
                display: (data) => data.activityTitle
            },
            {
                value: 'count',
                label: 'Nombre terminée',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.activities, "done_count"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.done_count, b.done_count),
                display: (data) => data.done_count
            },
            {
                value: 'current_count',
                label: 'Nombre en cours',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.activities, "current_count"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.current_count, b.current_count),
                display: (data) => data.current_count
            }
        ],
        activeUsersByClient: [
            {
                value: 'client_name',
                label: 'Établissement',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.activeUsersByClient, "client_name"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.client_name, b.client_name),
                display: (data) => {
                    if (!data.client_name) {
                        return '-';
                    }
                    return clientSlugs?.readOne && data.client_unique_id ?
                        <Link to={clientSlugs.readOne.replace(':uniqueId', data.client_unique_id)}
                            onClick={(e) => e.stopPropagation()}
                            key={'client-' + data.client_unique_id}>
                            {data.client_name}
                        </Link>
                        : <>{data.client_name}</>
                },
                flatDisplay: (data) => data.client_name ?? '-'
            },
            {
                value: 'count',
                label: 'Nombre de comptes utilisateur⋅ices actifs',
                test: HelperMetiers360.isArrayContainsValue(graphsData?.activeUsersByClient, "count"),
                method: (a, b) => HelperMetiers360.localeCompareWithNullable(a.count, b.count),
                display: (data) => data.count
            }
        ]
    };

  const currentActivitiesWithXPSelectionStepCount = globalCount?.current_activities_count ?
    Math.trunc(globalCount?.current_activities_with_xp_selection_step_count
      / globalCount?.current_activities_count * 100)
    : 0;

  return <>
        {Object.entries(globalCount)?.length > 0 && <Row>
            <b>Chiffres globaux :</b>
            <Col>
                <ul>
                    <li>Nombre d'activités terminées&nbsp;:&nbsp;<b>{globalCount?.activities_done_count}</b></li>
                    <li>Nombre d'activités en cours&nbsp;:&nbsp;<b>{globalCount?.current_activities_count}</b> (dont {currentActivitiesWithXPSelectionStepCount}% atteignent l'étape de sélection d'expériences immersives)</li>
                    <li>Nombre d'activités avec choix d'une expérience immersive&nbsp;:&nbsp;<b>{globalCount?.activities_done_count+globalCount?.current_activities_with_xp_selection_step_count}</b></li>
                </ul>
            </Col>
            <Col>
                <ul>
                    <li>Nombre de vues d'expériences immersives dans une activité&nbsp;:&nbsp;<b>{globalCount?.videos_watch_in_player_inside_activity_count}</b></li>
                    <li>Nombre de vues d'expériences immersives hors activité (par code d'accès direct)&nbsp;:&nbsp;<b>{globalCount?.videos_watch_in_player_outside_activity_count}</b></li>
                </ul>
            </Col>
        </Row>}
        <Row className="d-flex justify-content-between align-items-end mb-4">
            <Col>{dateFilter}</Col>
            <Col lg="4">{categoryFilter}</Col>
            <Col lg="2" className="d-flex justify-content-end">
                <Button id="btnInitFilters" className="shadow" variant="success" size="sm"
                    onClick={() => {
                        setDatesFilter([minDate, maxDate]);
                        setCategoriesFilter(selectedCategories?.map(cat => cat.name))
                    }}>
                    Filtrer
                </Button>
            </Col>
        </Row>
        {isFetching
            ? <Loader />
            : Object.keys(graphsData).length !== 0 && <>
                <Row>
                    <Col>
                        <ChartComponent
                            title="Top 5 des activités"
                            tableCsv={tableCsv?.activities}
                            csvSort={csvSort?.activities}
                            csvFilename="webapp_activités"
                            graphType="barTop5"
                            indexTable="activitySlug"
                            data={graphsData?.activities?.slice(0, 5)}
                            isDataAvailable={isAvailableData?.activities}
                            dataNamesLabel="activityTitle"
                            dataValuesLabel="done_count"
                            secondDataValuesLabel="current_count"
                            legendNames={{ done_count: "Terminée", current_count: "En cours" }}
                        />
                    </Col>
                    <Col>
                        <ChartComponent
                            title={"Top 5 des environnements pro " + romeTypeToDisplay}
                            tableCsv={romeTypeToDisplay === "découverts"
                                ? tableCsv?.discoveredRomes
                                : tableCsv?.favoriteRomes}
                            csvSort={romeTypeToDisplay === "découverts"
                                ? csvSort?.discoveredRomes
                                : csvSort?.favoriteRomes}
                            indexTable="code_rome"
                            csvFilename={"webapp_environnements_pro_" + romeTypeToDisplay}
                            graphType="barTop5"
                            data={(romeTypeToDisplay === "découverts"
                                ? tableCsv?.discoveredRomes
                                : tableCsv?.favoriteRomes
                            )?.slice(0, 5)}
                            isDataAvailable={romeTypeToDisplay === "découverts"
                                ? isAvailableData?.discoveredRomes
                                : isAvailableData?.favoriteRomes}
                            dataNamesLabel="label"
                            dataValuesLabel={romeTypeToDisplay === "découverts"
                                ? "count_discovered"
                                : "count_favorite"}
                            toggleButton={<ToggleButtonGroup type="radio" name="romeTypesGraph" onChange={(type) => setRomeTypeToDisplay(type)}
                                value={romeTypeToDisplay} className='ms-2'>
                                <ToggleButton value="découverts" key="rome_discovered" id="rome_discovered">Découverts</ToggleButton>
                                <ToggleButton value="favoris" key="rome_favorite" id="rome_favorite">Favoris</ToggleButton>
                            </ToggleButtonGroup>}
                        />
                    </Col>
                </Row>
                <Row className="mt-3">
                    <Col>
                        <ChartComponent
                            title="Top 5 des comptes utilisateur⋅ices par établissement (avec au - une activité terminée)"
                            tableCsv={tableCsv?.activeUsersByClient}
                            csvSort={csvSort?.activeUsersByClient}
                            indexTable="client_name"
                            csvFilename="webapp_comptes_utilisateurices_actifs_par_établissement"
                            graphType="barTop5"
                            data={graphsData?.activeUsersByClient?.slice(0, 5)}
                            isDataAvailable={isAvailableData?.activeUsersByClient}
                            dataNamesLabel="client_name"
                            dataValuesLabel="count"
                        />
                    </Col>
                    <Col>
                        <ChartComponent
                            title="Type d'appareils"
                            tableCsv={tableCsv?.deviceTypes}
                            csvSort={csvSort?.deviceTypes}
                            csvFilename="webapp_type_appareils"
                            graphType="pie"
                            data={graphsData?.deviceTypes}
                            isDataAvailable={isAvailableData?.deviceTypes}
                            dataNamesLabel="device"
                            dataValuesLabel="count_sessions"
                            legendNewlabels={deviceTypesLegendNewlabels}
                        />
                    </Col>
                </Row>
            </>
        }
    </>
}

export default WebappGlobalStatComponent;
